/**
 * @file     Attribution.tsx
 * @readme   ./README.md
 * @purpose  BCRES Rules §8 attribution line: "Listed by [agent], [office]. Data from BCRES. Last updated [ts]." Degrades gracefully when fields are null. The "Data from BCRES." source line is BCRES-IDX-only; DDF listings are attributed via the "Powered by REALTOR.ca" logo (DDF Rule 6) instead.
 */

'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { propertyDetailContent as C } from './pageContent';

type Props = {
  agentName: string | null;
  officeName: string | null;
  modificationTimestamp: string;
  /** Source feed. 'ddf' suppresses the "Data from BCRES." line. */
  feedProvider: string | null;
};

const TS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format the listing's modification timestamp deterministically.
 *
 * `toLocaleString` drifted between the server's Node ICU and the browser's ICU
 * ("Jun 8, 2026 at 12:10 AM" vs "Jun 8, 2026, 12:10 a.m."), causing a hydration
 * mismatch. We use Intl ONLY to shift into a fixed timezone and pull NUMERIC
 * fields (stable across ICU versions), then assemble all text — month, AM/PM,
 * separators — by hand so server and client render byte-identical output.
 */
function formatModificationTs(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const parts = new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: false,
      timeZone: 'America/Vancouver',
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const month = TS_MONTHS[Number(get('month')) - 1] ?? get('month');
    const day = get('day');
    const year = get('year');
    let hour = Number(get('hour')) % 24;
    const minute = get('minute');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${month} ${day}, ${year}, ${hour}:${minute} ${ampm}`;
  } catch {
    return iso;
  }
}

function collapseClosingPeriod(sentence: string): string {
  return sentence.endsWith('..') && !sentence.endsWith('...') ? sentence.slice(0, -1) : sentence;
}

export function Attribution({ agentName, officeName, modificationTimestamp, feedProvider }: Props) {
  const { t } = useTranslation();

  const formattedTs = formatModificationTs(modificationTimestamp);

  // Show brokerage name only (required by BCRES IDX rules).
  // Agent personal name omitted — not required for IDX display.
  // A brokerage name that already ends in a period ("Realty Corp.") must not
  // read "Corp.." once the sentence adds its own.
  const listedBy = officeName
    ? collapseClosingPeriod(
        t(C.attribution.listedByOfficeOnly.key, {
          defaultValue: C.attribution.listedByOfficeOnly.defaultValue,
          office: officeName,
        }),
      )
    : null;

  // "Data from BCRES." is the BCRES IDX (Bridge) source attribution. DDF
  // listings come from CREA's national pool, not BCRES, and carry their own
  // mandatory "Powered by REALTOR.ca" attribution (DDF Rule 6) — so suppress
  // this line for DDF rather than mis-attributing the source.
  const dataSource =
    feedProvider === 'ddf'
      ? null
      : t(C.attribution.dataSource.key, {
          defaultValue: C.attribution.dataSource.defaultValue,
        });
  const lastUpdated = t(C.attribution.lastUpdated.key, {
    defaultValue: C.attribution.lastUpdated.defaultValue,
    timestamp: formattedTs,
  });

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {[listedBy, dataSource, lastUpdated].filter(Boolean).join(' ')}
      </Typography>
    </Box>
  );
}
